import { useState, useEffect } from "react";
import { C, CATS, getCat, halalBucket, fmt, todayStr } from "./constants";
import { Btn, Inp, Sel, Label } from "./ui";

const sanitise = (str) => str.replace(/[<>"'`]/g, "").slice(0, 100);

const loadCatMemory = () => {
  try { return JSON.parse(localStorage.getItem("bb_cat_memory") || "{}"); } catch { return {}; }
};
const saveCatMemory = (desc, cat) => {
  const key = desc.toLowerCase().trim().slice(0, 30);
  if (!key) return;
  const mem = loadCatMemory();
  mem[key] = cat;
  localStorage.setItem("bb_cat_memory", JSON.stringify(mem));
};
const recallCat = (desc) => {
  const key = desc.toLowerCase().trim().slice(0, 30);
  if (!key) return null;
  return loadCatMemory()[key] || null;
};

// Default 6 categories shown before expanding
const DEFAULT_CATS = ["Food", "Groceries", "Transport", "Dining Out", "Shopping", "Other"];

function QuickAdd({ onAdd, creditCards, defaultDate, isFirst = false }) {
  const [type,        setType]     = useState("expense");
  const [amount,      setAmt]      = useState("");
  const [desc,        setDesc]     = useState("");
  const [cat,         setCat]      = useState(null);
  const [date,        setDate]     = useState(defaultDate || todayStr());
  const [cardId,      setCard]     = useState("none");
  const [done,        setDone]     = useState(false);
  const [showAllCats, setShowAll]  = useState(false);
  const [isRiba,      setIsRiba]   = useState(false);
  // Step 2 = riba nudge shown after saving
  const [ribaStep,    setRibaStep] = useState(false);
  const [savedRibaAmt, setSavedRibaAmt] = useState(0);

  useEffect(() => { setDate(defaultDate || todayStr()); }, [defaultDate]);

  // Reset riba toggle when switching away from income
  useEffect(() => { if (type !== "income") setIsRiba(false); }, [type]);

  useEffect(() => {
    if (desc.length < 3) return;
    const remembered = recallCat(desc);
    if (remembered) setCat(remembered);
  }, [desc]);

  const handle = () => {
    const a = parseFloat(amount);
    if (!a || a <= 0) return;
    const selectedCat = cat || "Other";
    const catObj = getCat(selectedCat);
    if (type === "expense" && desc.trim().length >= 3) saveCatMemory(desc, selectedCat);

    onAdd({
      id: Date.now(), date, amount: type === "expense" ? -a : a,
      description: desc || (type === "income" ? (isRiba ? "Interest Income" : "Income") : cat),
      expenseCategory: type === "expense" ? selectedCat : (isRiba ? "Interest" : "Income"),
      category: type === "income" ? "Income" : halalBucket[selectedCat] || "Want",
      icon: type === "income" ? (isRiba ? "⚠️" : "💰") : catObj.icon,
      cardId: type === "expense" && cardId !== "none" ? Number(cardId) : null,
      recurring: false,
      ...(isRiba && { isInterestIncome: true }),
    });

    if (isRiba && type === "income") {
      // Show donation nudge before closing
      setSavedRibaAmt(a);
      setDone(true);
      setTimeout(() => { setDone(false); setRibaStep(true); }, 400);
    } else {
      setDone(true); setTimeout(() => setDone(false), 800);
      setAmt(""); setDesc(""); setCard("none"); setShowAll(false); setIsRiba(false);
    }
  };

  const handleRibaDonate = () => {
    // Auto-create a matching Sadaqah expense
    onAdd({
      id: Date.now() + 1,
      date,
      amount: -savedRibaAmt,
      description: "Riba purification",
      expenseCategory: "Sadaqah",
      category: "Sadaqah",
      icon: "🤲",
      cardId: null,
      recurring: false,
      isRibaDonation: true,
    });
    setRibaStep(false);
    setAmt(""); setDesc(""); setCard("none"); setShowAll(false); setIsRiba(false);
  };

  const handleRibaSkip = () => {
    setRibaStep(false);
    setAmt(""); setDesc(""); setCard("none"); setShowAll(false); setIsRiba(false);
  };

  const visibleCats = showAllCats ? CATS : CATS.filter(c => DEFAULT_CATS.includes(c.id));

  // ── Riba donation nudge step ─────────────────────────────────
  if (ribaStep) {
    return (
      <div style={{
        background: C.surface2, border: `1px solid rgba(240,82,82,0.25)`,
        borderRadius: 14, padding: 20,
      }}>
        <div style={{ fontSize: 22, textAlign: "center", marginBottom: 10 }}>⚠️</div>
        <div style={{ color: C.red, fontWeight: 800, fontSize: 16, textAlign: "center", marginBottom: 12 }}>
          Riba recorded: {fmt(savedRibaAmt)}
        </div>
        <div style={{ color: C.text2, fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
          This money cannot be kept. Islamic scholars are unanimous: it must be donated to humanitarian causes —
          poverty relief, orphan care, food aid. This is wealth purification, not sadaqah, and does not count toward Zakat.
        </div>
        <button
          onClick={handleRibaDonate}
          style={{
            width: "100%", background: C.purple, border: "none", borderRadius: 12,
            color: "#fff", padding: "14px", fontSize: 14, fontWeight: 800, cursor: "pointer",
            marginBottom: 10,
          }}>
          ✓ Log donation now
        </button>
        <div style={{ color: C.text3, fontSize: 11, textAlign: "center", marginBottom: 10 }}>
          Creates a matching Sadaqah expense — net effect: zero
        </div>
        <button
          onClick={handleRibaSkip}
          style={{
            width: "100%", background: "none", border: `1px solid ${C.border}`,
            borderRadius: 12, color: C.text3, padding: "10px", fontSize: 13, cursor: "pointer",
          }}>
          Skip for now
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: done ? "rgba(61,214,140,0.06)" : C.surface2,
      border: `1px solid ${done ? "rgba(61,214,140,0.3)" : C.border}`,
      borderRadius: 14, padding: 16, transition: "all 0.3s"
    }}>

      {/* Type toggle */}
      <div style={{ display:"flex", background:C.surface, borderRadius:10, padding:3, gap:3, marginBottom:14 }}>
        {[["expense","💸 Expense"],["income","💰 Income"]].map(([v,l]) => (
          <button key={v} onClick={() => { setType(v); setCat(null); setShowAll(false); }} style={{
            flex:1, padding:"11px", fontSize:14, fontWeight:700, cursor:"pointer", borderRadius:8, border:"none",
            background: type===v ? (v==="income" ? "rgba(61,214,140,0.22)" : "rgba(240,82,82,0.22)") : "transparent",
            color: type===v ? (v==="income" ? C.green : C.red) : C.text3,
            transition:"all 0.15s",
          }}>{l}</button>
        ))}
      </div>

      {/* Amount — big and prominent */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", color:C.text3, marginBottom:6 }}>Amount</div>
        <input
          type="number" placeholder="0.00" value={amount}
          onChange={e => setAmt(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handle()}
          style={{
            width:"100%", background:C.surface, border:`1px solid ${C.border2}`,
            borderRadius:10, padding:"14px", fontSize:24, fontWeight:800,
            color: type==="income" ? C.green : C.red,
            outline:"none", boxSizing:"border-box",
          }}
        />
      </div>

      {/* Riba chip — income type only */}
      {type === "income" && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setIsRiba(p => !p)}
            style={{
              background: isRiba ? "rgba(240,82,82,0.08)" : "transparent",
              border: `1px solid ${isRiba ? "rgba(240,82,82,0.4)" : C.border}`,
              borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600,
              cursor: "pointer", color: isRiba ? C.red : C.text3,
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}>
            ⚠️ This is interest income (riba)?
          </button>

          {isRiba && (
            <div style={{
              marginTop: 10,
              background: "rgba(240,82,82,0.06)",
              border: "1px solid rgba(240,82,82,0.2)",
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ color: C.text2, fontSize: 12, lineHeight: 1.7 }}>
                Bank interest (riba) is not considered halal income. The scholarly consensus is to donate the full amount
                to charity (not as Sadaqah, but as purification). This app will exclude it from your wealth calculations.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category pills — expense only */}
      {type === "expense" && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {visibleCats.map(c => (
              <button key={c.id} onClick={() => setCat(c.id)} style={{
                background: cat===c.id ? c.color+"22" : "transparent",
                border: `1px solid ${cat===c.id ? c.color : C.border}`,
                color: cat===c.id ? c.color : C.text3,
                borderRadius:20, padding:"6px 12px", fontSize:13, cursor:"pointer",
                display:"flex", alignItems:"center", gap:4,
                minHeight:36, WebkitTapHighlightColor:"transparent",
              }}>{c.icon} {c.id}</button>
            ))}
            <button onClick={() => setShowAll(p => !p)} style={{
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.text3, borderRadius:20, padding:"6px 12px",
              fontSize:12, cursor:"pointer", minHeight:36,
            }}>
              {showAllCats ? "Less ↑" : "More ↓"}
            </button>
          </div>
        </div>
      )}

      {/* Description */}
      {!isFirst && (
        <div style={{ marginBottom:12 }}>
          <Inp label="Description"
            placeholder={type==="income" ? "Salary, freelance…" : "e.g. Tim Hortons"}
            value={desc}
            onChange={e => setDesc(sanitise(e.target.value))}
            onKeyDown={e => e.key==="Enter" && handle()}
          />
        </div>
      )}

      {/* Date + card */}
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:12 }}>
        <div>
          <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", color:C.text3, marginBottom:6 }}>Date</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ width:"100%", background:C.surface, border:`1px solid ${C.border2}`, borderRadius:10,
              padding:"11px 14px", color:C.text, fontSize:15, fontFamily:"'DM Sans',sans-serif",
              WebkitAppearance:"none", appearance:"none", colorScheme:"dark", outline:"none" }} />
        </div>
        {type==="expense" && creditCards.length>0 && (
          <Sel label="Charge to Card" value={cardId} onChange={e => setCard(e.target.value)}>
            <option value="none">— Cash / Debit —</option>
            {creditCards.map(c => <option key={c.id} value={c.id}>💳 {c.name} ({fmt(c.currentBalance)})</option>)}
          </Sel>
        )}
      </div>

      {type === "expense" && !cat && (
        <div style={{ fontSize:11, color:C.amber, textAlign:"center", marginBottom:8, fontWeight:600 }}>
          Pick a category above
        </div>
      )}

      <Btn onClick={handle} disabled={!amount || parseFloat(amount) <= 0 || (type==="expense" && !cat)} style={{ width:"100%" }}>
        {done ? "✓ Added!" : type==="income" ? "Add Income" : `Add ${cat} Expense`}
      </Btn>
    </div>
  );
}

export { QuickAdd };
